import {
    type FC,
    type ForwardedRef,
    type ReactNode,
    useCallback,
    useEffect,
} from 'react';
import { Button, ButtonProps } from 'primereact/button';
import { Button as CoreButton } from '@vox/frontend-react-ui';
import { canAccessPermission, useAdminAccess } from '@/shared/model/adminAccess';
import buttonClasses from '../../../styles/buttons.module.scss';

interface ActionButtonProps extends ButtonProps {
    children?: ReactNode;
    color?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
    fullWidth?: boolean;

    /** Отображает квадратную кнопку с иконкой и названием через aria-label */
    iconOnly?: boolean;

    /** Получает DOM-элемент кнопки для управления фокусом */
    buttonRef?: ForwardedRef<HTMLButtonElement>;

    /** Необходимое прикладное право */
    permission?: string;
}

/** Передаёт DOM-элемент кнопки вызывающему компоненту */
const assignButtonRef = (
    ref: ForwardedRef<HTMLButtonElement> | undefined,
    element: HTMLButtonElement | null
) => {
    if (typeof ref === 'function') ref(element);
    else if (ref) ref.current = element;
};

/** Единая кнопка действий с размерами и цветовыми ролями приложения. */
export const ActionButton: FC<ActionButtonProps> = (
    {
        size = 'normal',
        className = '',
        color = 'primary',
        fullWidth = false,
        iconOnly = false,
        children,
        buttonRef,
        visible,
        permission,
        ...buttonProps
    }
) => {
    const allowed = useAdminAccess((state) => (
        !permission || canAccessPermission(state.snapshot, permission)
    ));

    const setButtonRef = useCallback((button: Button | null) => {
        const element = allowed && visible !== false && button instanceof HTMLButtonElement
            ? button
            : null;

        assignButtonRef(buttonRef, element);
    }, [allowed, buttonRef, visible]);

    useEffect(() => () => assignButtonRef(buttonRef, null), [buttonRef]);

    const buttonClass = [
        buttonClasses.actionButton,
        buttonClasses[`actionButton--${size}`],
        buttonClasses[`actionButton--${color}`],
        fullWidth && buttonClasses['actionButton--fullWidth'],
        iconOnly && buttonClasses['actionButton--iconOnly'],
        className,
    ].filter(Boolean)
        .join(' ');

    return (
        <CoreButton
            aria-busy={buttonProps['aria-busy']}
            loading={buttonProps.loading}
            disabled={buttonProps.disabled}
            type={buttonProps.type ?? 'submit'}
            renderButton={(sharedProps) => (
                <Button
                    {...buttonProps}
                    {...sharedProps}
                    pt={{
                        icon: { 'aria-hidden': true },
                        loadingIcon: { 'aria-hidden': true },
                        ...buttonProps.pt
                    }}
                    ref={setButtonRef}
                    className={buttonClass}
                    visible={allowed && visible !== false}
                >
                    {children}
                </Button>
            )}
        />
    );
};
